/**
 * Chat Tool Runs
 *
 * Persists tool execution progress for chat streams.
 *
 * Primary responsibilities:
 * - Create durable run records for chat tool streams
 * - Persist each tool step as it starts, completes, or fails
 * - Build recovery context when a prior stream timed out mid-synthesis
 *
 * Dependencies:
 * - Supabase service client
 */

import type { SupabaseClient } from '@supabase/supabase-js'

import { createServiceClient } from '@/lib/supabase/service-client'
import { sanitizeNullBytes } from '@/lib/utils/sanitize-null-bytes'
import type { Database, Json } from '@/supabase/types'

const DEFAULT_RUN_LIMIT = 5
const MAX_RUN_LIMIT = 20
const RECOVERY_WINDOW_MS = 24 * 60 * 60 * 1000
const RECOVERY_RESULT_MAX_CHARS = 1_200
const RECOVERY_STEP_LIMIT = 8
// A continuation that was claimed (continuation_status = 'running') but whose
// executing function died without finishing would otherwise strand the run
// forever — nothing resets it and only 'queued' runs get picked up again.
// After this window a claimed continuation is treated as dead and re-queued.
const STALE_RUNNING_CONTINUATION_MS = 3 * 60 * 1000
// Max CONSECUTIVE no-progress continuation legs before a run is failed. A
// leg that completes new tool steps resets this to 0, so a genuinely long
// task continues indefinitely; only a run that keeps dying without completing
// anything new is stopped — bounding crash loops without capping real work.
const MAX_NO_PROGRESS_CONTINUATION_ATTEMPTS = 3

export type ChatToolRunStatus = 'running' | 'completed' | 'failed' | 'aborted'
export type ChatToolRunStepStatus = 'started' | 'completed' | 'failed'
export type ChatToolRunContinuationStatus =
  | 'not_needed'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'

export type ChatToolRunRow = {
  id: string
  user_id: string
  chat_id: string
  workspace_id: string | null
  space_id: string | null
  source: string | null
  model: string | null
  request_fingerprint: string | null
  status: ChatToolRunStatus
  assistant_message_id: string | null
  continuation_status: ChatToolRunContinuationStatus
  continuation_attempts: number
  continuation_started_at: string | null
  continuation_completed_at: string | null
  continuation_error: string | null
  last_error: string | null
  metadata: Json
  started_at: string
  finished_at: string | null
  created_at: string
  updated_at: string
}

export type ChatToolRunStepRow = {
  id: string
  run_id: string
  user_id: string
  chat_id: string
  tool_call_id: string | null
  platform_tool_id: string | null
  tool_name: string
  sequence: number
  source: string | null
  display_title: string | null
  status: ChatToolRunStepStatus
  request_payload: Json
  result_payload: Json | null
  result_text: string | null
  result_processing_mode: string | null
  error: string | null
  duration_ms: number | null
  metadata: Json
  started_at: string
  completed_at: string | null
  created_at: string
  updated_at: string
}

export type ChatToolRunWithSteps = ChatToolRunRow & {
  steps: ChatToolRunStepRow[]
}

type ChatToolRunInsert = {
  user_id: string
  chat_id: string
  workspace_id?: string | null
  space_id?: string | null
  source?: string | null
  model?: string | null
  request_fingerprint?: string | null
  status?: ChatToolRunStatus
  assistant_message_id?: string | null
  continuation_status?: ChatToolRunContinuationStatus
  continuation_attempts?: number
  continuation_started_at?: string | null
  continuation_completed_at?: string | null
  continuation_error?: string | null
  last_error?: string | null
  metadata?: Json
}

type ChatToolRunUpdate = Partial<
  Pick<
    ChatToolRunRow,
    | 'status'
    | 'assistant_message_id'
    | 'continuation_status'
    | 'continuation_attempts'
    | 'continuation_started_at'
    | 'continuation_completed_at'
    | 'continuation_error'
    | 'last_error'
    | 'finished_at'
    | 'metadata'
  >
>

type ChatToolRunStepInsert = {
  run_id: string
  user_id: string
  chat_id: string
  tool_call_id?: string | null
  platform_tool_id?: string | null
  tool_name: string
  sequence?: number
  source?: string | null
  display_title?: string | null
  status?: ChatToolRunStepStatus
  request_payload?: Json
  result_payload?: Json | null
  result_text?: string | null
  result_processing_mode?: string | null
  error?: string | null
  duration_ms?: number | null
  metadata?: Json
}

type ChatToolRunStepUpdate = Partial<
  Pick<
    ChatToolRunStepRow,
    | 'status'
    | 'result_payload'
    | 'result_text'
    | 'result_processing_mode'
    | 'error'
    | 'duration_ms'
    | 'completed_at'
    | 'metadata'
  >
>

type ChatToolRunTable = {
  Row: ChatToolRunRow
  Insert: ChatToolRunInsert
  Update: ChatToolRunUpdate
  Relationships: []
}

type ChatToolRunStepTable = {
  Row: ChatToolRunStepRow
  Insert: ChatToolRunStepInsert
  Update: ChatToolRunStepUpdate
  Relationships: []
}

type ChatToolRunDatabase = Database & {
  public: Database['public'] & {
    Tables: Database['public']['Tables'] & {
      chat_tool_runs: ChatToolRunTable
      chat_tool_run_steps: ChatToolRunStepTable
    }
  }
}

type ChatToolRunClient = SupabaseClient<ChatToolRunDatabase>

export type CreateChatToolRunInput = {
  userId: string
  chatId: string
  workspaceId?: string | null
  spaceId?: string | null
  source?: string | null
  model?: string | null
  requestFingerprint?: string | null
  assistantMessageId?: string | null
  metadata?: unknown
}

export type RecordChatToolRunStepInput = {
  runId: string
  userId: string
  chatId: string
  toolCallId?: string | null
  platformToolId?: string | null
  toolName: string
  sequence: number
  source?: string | null
  displayTitle?: string | null
  request?: unknown
  metadata?: unknown
}

export type CompleteChatToolRunStepInput = {
  stepId?: string | null
  runId: string
  userId: string
  toolCallId?: string | null
  result?: unknown
  resultText?: string | null
  resultProcessingMode?: string | null
  durationMs?: number | null
  metadata?: unknown
}

export type FailChatToolRunStepInput = {
  stepId?: string | null
  runId: string
  userId: string
  toolCallId?: string | null
  error: string
  durationMs?: number | null
  metadata?: unknown
}

// --------------

function getChatToolRunClient(): ChatToolRunClient {
  return createServiceClient() as unknown as ChatToolRunClient
}

function getMetadataRecord(metadata: Json | null | undefined): Record<string, Json> | null {
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? (metadata as Record<string, Json>)
    : null
}

function toJson(value: unknown): Json {
  if (value === undefined) {
    return null
  }

  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return sanitizeNullBytes(value) as Json
  }

  try {
    return sanitizeNullBytes(JSON.parse(JSON.stringify(value))) as Json
  } catch {
    return { value: sanitizeNullBytes(String(value)) }
  }
}

function normalizeLimit(limit: number | null | undefined) {
  if (!Number.isFinite(limit) || (limit ?? 0) < 1) {
    return DEFAULT_RUN_LIMIT
  }

  return Math.min(Math.trunc(limit ?? DEFAULT_RUN_LIMIT), MAX_RUN_LIMIT)
}

function getRecoverySinceIso() {
  return new Date(Date.now() - RECOVERY_WINDOW_MS).toISOString()
}

function truncateText(value: string, maxChars: number) {
  return value.length > maxChars ? `${value.slice(0, maxChars)}...` : value
}

function stepResultText(step: ChatToolRunStepRow) {
  if (step.result_text?.trim()) {
    return step.result_text.trim()
  }

  if (step.result_payload == null) {
    return ''
  }

  return JSON.stringify(step.result_payload)
}

export function buildChatToolRunStepResultText(step: ChatToolRunStepRow) {
  return stepResultText(step)
}

function applyStepMatch<T>(
  query: T & {
    eq: (column: string, value: string) => T
  },
  input: {
    stepId?: string | null
    runId: string
    toolCallId?: string | null
  }
) {
  if (input.stepId) {
    return query.eq('id', input.stepId)
  }

  if (input.toolCallId) {
    return query.eq('run_id', input.runId).eq('tool_call_id', input.toolCallId)
  }

  return query.eq('run_id', input.runId)
}

// --------------

export async function createChatToolRun(
  input: CreateChatToolRunInput
): Promise<ChatToolRunRow> {
  const supabase = getChatToolRunClient()
  const { data, error } = await supabase
    .from('chat_tool_runs')
    .insert({
      user_id: input.userId,
      chat_id: input.chatId,
      workspace_id: input.workspaceId ?? null,
      space_id: input.spaceId ?? null,
      source: input.source ?? null,
      model: input.model ?? null,
      request_fingerprint: input.requestFingerprint ?? null,
      status: 'running',
      assistant_message_id: input.assistantMessageId ?? null,
      continuation_status: input.assistantMessageId ? 'queued' : 'not_needed',
      metadata: toJson(input.metadata ?? {})
    })
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return data
}

// Proactively hands a still-running run off for continuation instead of
// waiting for the staleness cron to notice it's gone quiet — used when the
// request itself detects it's approaching its own time budget and wants to
// checkpoint + signal the client to fire a follow-up request immediately.
//
// `fromRunning: true` also re-queues a run whose continuation is currently
// 'running'. This is only safe from INSIDE the authoritative leg that is
// itself that running continuation and is handing off to the next leg (a
// deadline re-stop) — never from a pre-claim check, which would let two legs
// run at once.
export async function queueChatToolRunContinuation(input: {
  runId: string
  userId: string
  fromRunning?: boolean
}): Promise<void> {
  const supabase = getChatToolRunClient()
  const allowedFrom: ChatToolRunContinuationStatus[] = input.fromRunning
    ? ['not_needed', 'queued', 'running']
    : ['not_needed', 'queued']
  const { error } = await supabase
    .from('chat_tool_runs')
    .update({
      continuation_status: 'queued'
    })
    .eq('id', input.runId)
    .eq('user_id', input.userId)
    .in('continuation_status', allowedFrom)

  if (error) {
    throw new Error(error.message)
  }
}

// Re-queues continuations that were claimed ('running') but whose executing
// function died without finishing (stale), so a crashed/killed continuation
// leg becomes recoverable instead of stranding the run forever. Runs past the
// attempt cap are marked failed rather than retried into an infinite loop.
export async function recoverStaleRunningContinuations(input?: {
  staleBefore?: Date
  attemptCap?: number
}): Promise<void> {
  const supabase = getChatToolRunClient()
  const staleBefore = (
    input?.staleBefore ?? new Date(Date.now() - STALE_RUNNING_CONTINUATION_MS)
  ).toISOString()
  // Attempts are consecutive-no-progress (reset on progress in claim), so a
  // stale leg that had been making progress carries a low count and gets
  // re-queued; only a stalled/crash-looping one is past the cap and failed.
  const attemptCap = input?.attemptCap ?? MAX_NO_PROGRESS_CONTINUATION_ATTEMPTS

  const { error: requeueError } = await supabase
    .from('chat_tool_runs')
    .update({ continuation_status: 'queued' })
    .eq('continuation_status', 'running')
    .in('status', ['running', 'failed'])
    .lt('continuation_started_at', staleBefore)
    .lt('continuation_attempts', attemptCap)

  if (requeueError) {
    throw new Error(requeueError.message)
  }

  const { error: failError } = await supabase
    .from('chat_tool_runs')
    .update({
      continuation_status: 'failed',
      continuation_error: 'Continuation stalled without making progress',
      finished_at: new Date().toISOString()
    })
    .eq('continuation_status', 'running')
    .in('status', ['running', 'failed'])
    .lt('continuation_started_at', staleBefore)
    .gte('continuation_attempts', attemptCap)

  if (failError) {
    throw new Error(failError.message)
  }
}

// A pending assistant message is created (empty content, metadata.streamStatus
// = 'running') at the start of every turn and finalized when the turn
// completes. When a tool run is interrupted or crosses the server's
// maxDuration/continuation boundary, the client's stream handler can neither
// finalize the row nor hit its catch cleanup — so the row is orphaned forever
// as an EMPTY 'running' message. On reload it renders as a broken
// checkpoint/duplicate and its would-be content/annotation are lost.
//
// This deletes those orphans: assistant messages with empty content whose
// metadata.streamStatus is still 'running' past the stale window. A row that
// genuinely finished will have content and/or a terminal streamStatus, so it
// is never touched. Non-empty rows are also left alone (partial content is
// preferable to deletion). Returns the number of rows deleted.
export async function reconcileStaleRunningAssistantMessages(input?: {
  staleBefore?: Date
  chatId?: string
}): Promise<number> {
  const supabase = getChatToolRunClient()
  const staleBefore = (
    input?.staleBefore ?? new Date(Date.now() - STALE_RUNNING_CONTINUATION_MS)
  ).toISOString()

  let query = supabase
    .from('messages')
    .select('id, chat_id, content, metadata, created_at')
    .eq('role', 'assistant')
    .eq('content', '')
    .lt('created_at', staleBefore)
    .limit(200)

  if (input?.chatId) {
    query = query.eq('chat_id', input.chatId)
  }

  const { data: rows, error } = await query

  if (error) {
    throw new Error(error.message)
  }

  if (!rows || rows.length === 0) {
    return 0
  }

  // Only delete rows that are BOTH empty and still marked running (no terminal
  // marker). Filter in JS so we can safely read the jsonb metadata shape.
  const orphanIds = rows
    .filter((row) => {
      const metadata =
        row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
          ? (row.metadata as Record<string, unknown>)
          : null
      const streamStatus = metadata?.streamStatus
      const content = typeof row.content === 'string' ? row.content.trim() : ''
      return content.length === 0 && streamStatus === 'running'
    })
    .map((row) => row.id)

  if (orphanIds.length === 0) {
    return 0
  }

  const { error: deleteError } = await supabase
    .from('messages')
    .delete()
    .in('id', orphanIds)

  if (deleteError) {
    throw new Error(deleteError.message)
  }

  return orphanIds.length
}

export async function markChatToolRunFinished(input: {
  runId: string
  userId: string
  status: ChatToolRunStatus
  lastError?: string | null
}): Promise<void> {
  const supabase = getChatToolRunClient()
  const terminalContinuationStatus =
    input.status === 'completed' || input.status === 'aborted'
      ? 'not_needed'
      : undefined
  const { error } = await supabase
    .from('chat_tool_runs')
    .update({
      status: input.status,
      finished_at: new Date().toISOString(),
      last_error: input.lastError ? sanitizeNullBytes(input.lastError) : null,
      ...(terminalContinuationStatus
        ? { continuation_status: terminalContinuationStatus }
        : {})
    })
    .eq('id', input.runId)
    .eq('user_id', input.userId)

  if (error) {
    throw new Error(error.message)
  }
}

export async function recordChatToolRunStepStarted(
  input: RecordChatToolRunStepInput
): Promise<ChatToolRunStepRow> {
  const supabase = getChatToolRunClient()
  const { data, error } = await supabase
    .from('chat_tool_run_steps')
    .insert({
      run_id: input.runId,
      user_id: input.userId,
      chat_id: input.chatId,
      tool_call_id: input.toolCallId ?? null,
      platform_tool_id: input.platformToolId ?? null,
      tool_name: sanitizeNullBytes(input.toolName),
      sequence: input.sequence,
      source: input.source ? sanitizeNullBytes(input.source) : null,
      display_title: input.displayTitle
        ? sanitizeNullBytes(input.displayTitle)
        : null,
      status: 'started',
      request_payload: toJson(input.request ?? {}),
      metadata: toJson(input.metadata ?? {})
    })
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return data
}

export async function recordChatToolRunStepCompleted(
  input: CompleteChatToolRunStepInput
): Promise<void> {
  const supabase = getChatToolRunClient()
  const update = {
    status: 'completed' as const,
    result_payload: toJson(input.result ?? null),
    result_text: input.resultText ? sanitizeNullBytes(input.resultText) : null,
    result_processing_mode: input.resultProcessingMode ?? null,
    error: null,
    duration_ms: input.durationMs ?? null,
    completed_at: new Date().toISOString(),
    metadata: toJson(input.metadata ?? {})
  }
  const query = supabase
    .from('chat_tool_run_steps')
    .update(update)
    .eq('user_id', input.userId)
  const { error } = await applyStepMatch(query, input)

  if (error) {
    throw new Error(error.message)
  }
}

export async function recordChatToolRunStepFailed(
  input: FailChatToolRunStepInput
): Promise<void> {
  const supabase = getChatToolRunClient()
  const update = {
    status: 'failed' as const,
    error: sanitizeNullBytes(input.error),
    duration_ms: input.durationMs ?? null,
    completed_at: new Date().toISOString(),
    metadata: toJson(input.metadata ?? {})
  }
  const query = supabase
    .from('chat_tool_run_steps')
    .update(update)
    .eq('user_id', input.userId)
  const { error } = await applyStepMatch(query, input)

  if (error) {
    throw new Error(error.message)
  }
}

export async function listChatToolRunsWithSteps(input: {
  userId: string
  chatId: string
  limit?: number
  statuses?: ChatToolRunStatus[]
}): Promise<ChatToolRunWithSteps[]> {
  const supabase = getChatToolRunClient()
  const statuses = input.statuses?.length ? input.statuses : null
  let request = supabase
    .from('chat_tool_runs')
    .select('*')
    .eq('user_id', input.userId)
    .eq('chat_id', input.chatId)
    .gte('created_at', getRecoverySinceIso())
    .order('created_at', { ascending: false })
    .limit(normalizeLimit(input.limit))

  if (statuses) {
    request = request.in('status', statuses)
  }

  const { data: runs, error: runsError } = await request

  if (runsError) {
    throw new Error(runsError.message)
  }

  if (!runs?.length) {
    return []
  }

  const runIds = runs.map((run) => run.id)
  const { data: steps, error: stepsError } = await supabase
    .from('chat_tool_run_steps')
    .select('*')
    .eq('user_id', input.userId)
    .in('run_id', runIds)
    .order('sequence', { ascending: true })
    .order('created_at', { ascending: true })

  if (stepsError) {
    throw new Error(stepsError.message)
  }

  const stepsByRun = new Map<string, ChatToolRunStepRow[]>()

  for (const step of steps ?? []) {
    const existing = stepsByRun.get(step.run_id) ?? []
    existing.push(step)
    stepsByRun.set(step.run_id, existing)
  }

  return runs.map((run) => ({
    ...run,
    steps: stepsByRun.get(run.id) ?? []
  }))
}

export async function getChatToolRunWithSteps(input: {
  runId: string
}): Promise<ChatToolRunWithSteps | null> {
  const supabase = getChatToolRunClient()
  const { data: run, error: runError } = await supabase
    .from('chat_tool_runs')
    .select('*')
    .eq('id', input.runId)
    .maybeSingle()

  if (runError) {
    throw new Error(runError.message)
  }

  if (!run) {
    return null
  }

  const { data: steps, error: stepsError } = await supabase
    .from('chat_tool_run_steps')
    .select('*')
    .eq('run_id', run.id)
    .order('sequence', { ascending: true })
    .order('created_at', { ascending: true })

  if (stepsError) {
    throw new Error(stepsError.message)
  }

  return {
    ...run,
    steps: steps ?? []
  }
}

export async function listDueChatToolRunContinuations(input?: {
  staleBefore?: Date
  limit?: number
}): Promise<ChatToolRunRow[]> {
  const supabase = getChatToolRunClient()
  const staleBefore = input?.staleBefore ?? new Date(Date.now() - 6 * 60 * 1000)
  // Include native iOS detach runs (no assistant_message_id). Those used to
  // be invisible to cron — only the in-process detached driver could finish
  // them, so a container restart left the user message unanswered forever.
  const { data, error } = await supabase
    .from('chat_tool_runs')
    .select('*')
    .eq('continuation_status', 'queued')
    .in('status', ['running', 'failed'])
    .lt('updated_at', staleBefore.toISOString())
    .order('updated_at', { ascending: true })
    .limit(normalizeLimit(input?.limit))

  if (error) {
    throw new Error(error.message)
  }

  return data ?? []
}

/**
 * Native detach turns that died without ever being queued for continuation.
 *
 * Symptom: status=running, continuation_status=not_needed, requestSnapshot
 * present (often detachOnDisconnect:true), user message persisted, no
 * assistant reply. Happens when the client disconnects before tools-stream's
 * abort/queue hooks run (new-chat early exit, hard kill, cold process death).
 *
 * Only select rows stale enough that the original request is almost certainly
 * dead — promoting a live turn would double-execute.
 */
export async function listOrphanedDetachedChatToolRuns(input?: {
  staleBefore?: Date
  limit?: number
}): Promise<ChatToolRunRow[]> {
  const supabase = getChatToolRunClient()
  // Match DEFAULT_STALE_MS in tool-run-continuation — original request budget.
  const staleBefore = input?.staleBefore ?? new Date(Date.now() - 45 * 1000)
  const { data, error } = await supabase
    .from('chat_tool_runs')
    .select('*')
    .eq('status', 'running')
    .eq('continuation_status', 'not_needed')
    .is('assistant_message_id', null)
    .lt('updated_at', staleBefore.toISOString())
    .order('updated_at', { ascending: true })
    .limit(normalizeLimit(input?.limit ?? 5))

  if (error) {
    throw new Error(error.message)
  }

  // Prefer runs that opted into detach (native) and still have a replay body.
  return (data ?? []).filter((run) => {
    const meta =
      run.metadata && typeof run.metadata === 'object' && !Array.isArray(run.metadata)
        ? (run.metadata as Record<string, unknown>)
        : null
    const snapshot = meta?.requestSnapshot
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
      return false
    }
    const body = snapshot as Record<string, unknown>
    return body.detachOnDisconnect === true
  })
}

/** Latest open run for a chat — used by resume-by-chatId when the phone never saw runId. */
export async function findOpenChatToolRunForChat(input: {
  userId: string
  chatId: string
}): Promise<ChatToolRunRow | null> {
  const supabase = getChatToolRunClient()
  const { data, error } = await supabase
    .from('chat_tool_runs')
    .select('*')
    .eq('user_id', input.userId)
    .eq('chat_id', input.chatId)
    .in('status', ['running', 'failed'])
    .in('continuation_status', ['not_needed', 'queued', 'running'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    throw new Error(error.message)
  }

  return data ?? null
}

// Releases a claimed continuation back to 'queued' when the HTTP leg dies
// before tools-stream can hand off (client disconnect during preflight,
// thrown setup error, cancelled response body). Only matches rows that are
// still claimed AND still overall running — completed / deadline-queued /
// failed rows are left alone so happy-path finish and deadline-stop stay safe.
export async function releaseClaimedChatToolRunContinuation(input: {
  runId: string
  userId?: string
  reason?: string
}): Promise<boolean> {
  const supabase = getChatToolRunClient()
  let query = supabase
    .from('chat_tool_runs')
    .update({
      continuation_status: 'queued',
      ...(input.reason
        ? { continuation_error: sanitizeNullBytes(input.reason) }
        : {})
    })
    .eq('id', input.runId)
    .eq('continuation_status', 'running')
    .eq('status', 'running')

  if (input.userId) {
    query = query.eq('user_id', input.userId)
  }

  const { data, error } = await query.select('id').maybeSingle()

  if (error) {
    throw new Error(error.message)
  }

  return Boolean(data)
}

export async function claimChatToolRunContinuation(input: {
  runId: string
}): Promise<ChatToolRunRow | null> {
  const supabase = getChatToolRunClient()
  const { data: current, error: readError } = await supabase
    .from('chat_tool_runs')
    .select('*')
    .eq('id', input.runId)
    .eq('continuation_status', 'queued')
    .maybeSingle()

  if (readError) {
    throw new Error(readError.message)
  }

  if (!current) {
    return null
  }

  // Progress-aware attempt cap. continuation_attempts counts CONSECUTIVE
  // no-progress legs, not total legs — a genuinely long task (seen in prod:
  // 184 completed tool steps across many legs) must be able to continue
  // indefinitely as long as each leg keeps completing new work, while a leg
  // that keeps dying without completing anything new is still bounded so it
  // can't crash-loop. We compare the run's completed-step count against a
  // watermark stored in metadata: more steps than last time => reset the
  // counter; no new steps => it stalled, so count it toward the cap.
  const { count: completedStepCount, error: countError } = await supabase
    .from('chat_tool_run_steps')
    .select('id', { count: 'exact', head: true })
    .eq('run_id', input.runId)
    .eq('status', 'completed')

  if (countError) {
    throw new Error(countError.message)
  }

  const completedSteps = completedStepCount ?? 0
  const metadata = getMetadataRecord(current.metadata) ?? {}
  const progressWatermark =
    typeof metadata.continuationProgressAt === 'number' ? metadata.continuationProgressAt : 0
  const madeProgress = completedSteps > progressWatermark
  const nextAttempts = madeProgress ? 0 : current.continuation_attempts + 1

  if (nextAttempts >= MAX_NO_PROGRESS_CONTINUATION_ATTEMPTS) {
    await supabase
      .from('chat_tool_runs')
      .update({
        continuation_status: 'failed',
        continuation_error: 'Continuation stalled without making progress',
        finished_at: new Date().toISOString()
      })
      .eq('id', input.runId)
      .eq('continuation_status', 'queued')
    return null
  }

  const { data, error } = await supabase
    .from('chat_tool_runs')
    .update({
      continuation_status: 'running',
      continuation_started_at: new Date().toISOString(),
      continuation_attempts: nextAttempts,
      continuation_error: null,
      metadata: { ...metadata, continuationProgressAt: completedSteps } as Json
    })
    .eq('id', input.runId)
    .eq('continuation_status', 'queued')
    .select('*')
    .maybeSingle()

  if (error) {
    throw new Error(error.message)
  }

  return data ?? null
}

export async function completeChatToolRunContinuation(input: {
  runId: string
  finalStatus?: ChatToolRunStatus
}): Promise<void> {
  const supabase = getChatToolRunClient()
  const { error } = await supabase
    .from('chat_tool_runs')
    .update({
      status: input.finalStatus ?? 'completed',
      continuation_status: 'completed',
      continuation_completed_at: new Date().toISOString(),
      continuation_error: null,
      finished_at: new Date().toISOString(),
      last_error: null
    })
    .eq('id', input.runId)

  if (error) {
    throw new Error(error.message)
  }
}

export async function failChatToolRunContinuation(input: {
  runId: string
  error: string
}): Promise<void> {
  const supabase = getChatToolRunClient()
  const { error } = await supabase
    .from('chat_tool_runs')
    .update({
      continuation_status: 'failed',
      continuation_completed_at: new Date().toISOString(),
      continuation_error: input.error,
      last_error: input.error
    })
    .eq('id', input.runId)

  if (error) {
    throw new Error(error.message)
  }
}

export async function buildChatToolRunRecoveryContext(input: {
  userId: string
  chatId: string
}): Promise<string | null> {
  const runs = await listChatToolRunsWithSteps({
    userId: input.userId,
    chatId: input.chatId,
    statuses: ['running', 'failed', 'aborted'],
    limit: 3
  })
  const completedSteps = runs
    .flatMap((run) =>
      run.steps
        .filter((step) => step.status === 'completed')
        .map((step) => ({ run, step }))
    )
    .slice(0, RECOVERY_STEP_LIMIT)

  if (completedSteps.length === 0) {
    return null
  }

  const lines = completedSteps.map(({ run, step }) => {
    const result = truncateText(stepResultText(step), RECOVERY_RESULT_MAX_CHARS)
    return [
      `- Run ${run.id} (${run.status}), tool ${step.tool_name}, sequence ${step.sequence}`,
      `  Result: ${result || '(completed with no text result)'}`
    ].join('\n')
  })

  return [
    '\n\nPersisted tool results from a previous interrupted run:',
    'These tool calls already completed server-side within the last 24 hours.',
    'Use these results before rerunning the same tool work. If a tool mutation',
    'already happened, do not repeat it unless the user explicitly asks.',
    ...lines
  ].join('\n')
}
